import { Component, OnInit, ViewChild } from '@angular/core';
import { ExportInterfaceConfigurationDTO, ExportInterfaceConfigurationFileProcessType, ExportInterfaceConfigurationTransferType, ExportInterfaceConfigurationType, UpdateExportInterfaceConfigTemplateDTO } from '../../../base/models/models.swagger.generated';
import { ConfigurationFormCoordinatorService } from '../shared/configuration-forms/configuration-form-coordinator.service';
import { ConfigurationTemplatesService } from './configuration-templates.service';
import { ConfigurationTemplatesOverviewComponent } from '../shared/configuration-templates-overview/configuration-templates-overview.component';
import { ErrorType } from '../../../core/routing/error-types';
import { UntypedFormGroup } from '@angular/forms';
import { ToastService } from '../../../base/services/toast.service';
import { ModalService } from '../../../base/shared/modal/modal.service';
import { merge, Observable } from 'rxjs';
import { filter, mapTo } from 'rxjs/operators';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { ConfigurationFormsOverviewComponent } from '../shared/configuration-forms/configuration-forms-overview/configuration-forms-overview.component';

import { TitleBarComponent } from '../../../base/shared/title-bar/title-bar.component';
import { configurationTemplatesExpConfRoute } from 'src/app/core/routing/route-names';
import { CommonModule } from '@angular/common';

@Component({
    selector: 'app-configuration-templates',
    templateUrl: './configuration-templates.component.html',
    styleUrls: ['./configuration-templates.component.scss'],
    imports: [
    TitleBarComponent,
    ConfigurationTemplatesOverviewComponent,
    ConfigurationFormsOverviewComponent,
    TranslateModule,
    CommonModule
]
})
export class ConfigurationTemplatesComponent implements OnInit {

  @ViewChild(ConfigurationTemplatesOverviewComponent) configurationTemplatesSelectionComponent: ConfigurationTemplatesOverviewComponent;

  @ViewChild(ConfigurationFormsOverviewComponent)
  configurationFormsOverviewComponent: ConfigurationFormsOverviewComponent;

  public configuration$ = this.configurationFormCoordinatorService.configurationDetailsDTOUpdated.asObservable();

  public get configuration(): ExportInterfaceConfigurationDTO {
    return this.configurationFormCoordinatorService.configurationDetailsDTO;
  }

  public get configurationFormGeneral(): UntypedFormGroup {
    return this.configurationFormCoordinatorService.configurationFormGeneral;
  }

  public get configurationFormTimeplan(): UntypedFormGroup {
    return this.configurationFormCoordinatorService.configurationFormTimeplan;
  }

  public get configurationFormEmail(): UntypedFormGroup {
    return this.configurationFormCoordinatorService.configurationFormEmail;
  }

  public get configurationFormFTP(): UntypedFormGroup {
    return this.configurationFormCoordinatorService.configurationFormFTP;
  }

  public get configurationFormREST(): UntypedFormGroup {
    return this.configurationFormCoordinatorService.configurationFormREST;
  }

  public get configurationFormFileName(): UntypedFormGroup {
    return this.configurationFormCoordinatorService.configurationFormFileName;
  }

  public get configurationFormFileFilter(): UntypedFormGroup {
    return this.configurationFormCoordinatorService.configurationFormFileFilter;
  }

  public get configurationFormFileFields(): UntypedFormGroup {
    return this.configurationFormCoordinatorService.configurationFormFileFields;
  }

  public selectedTemplateId : number;

  constructor(public configurationFormCoordinatorService: ConfigurationFormCoordinatorService,
    public configurationTemplateService: ConfigurationTemplatesService,
    private toastService: ToastService,
    private modalService: ModalService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
  ) {
  }

  ngOnInit() {
    this.configurationFormCoordinatorService.configurationDetailsDTO = null;
    this.configurationFormCoordinatorService.configurationDetailsDTOUpdated.next(null);
    this.configurationFormCoordinatorService.resetForms();
    this.configurationFormCoordinatorService.isTemplateMode = true;
    this.activatedRoute.data.subscribe(data => {
      if (data.initialize) {
        this.configurationTemplateService.configurationType = null;
      }
    });

    this.router.events.pipe(filter(event => event instanceof NavigationEnd && event.url.includes(configurationTemplatesExpConfRoute))).subscribe(() => {
      this.urlChanged();
    });

    // call the first time urlChanged on our own
    this.urlChanged();
  }

  public urlChanged(): void {
    if (this.activatedRoute.children.length > 0 && this.activatedRoute?.snapshot?.firstChild?.url[0]?.path) {
      var id = this.activatedRoute?.snapshot?.firstChild?.url[0]?.path;
      if (id != null && id != undefined && !isNaN(+id)){
        this.selectedTemplateId = +id;
      } else {
        this.selectedTemplateId = null;
      }
    } else {
      this.selectedTemplateId = null;
    }
  }

  public addConfigurationTemplate() {
    this.configurationTemplateService.addRecord().subscribe({
      next: (data) => {
        this.configurationTemplatesSelectionComponent.recordAdded(data.id);
      }, error: (error) => {
        // we just catch since warnings are handled in service
      }
    });
  }

  public deleteConfigurationTemplate() {
    const dialog = this.modalService.showOkCancelDialog('i18n.domain.export-configuration.configurations.delete-dialog.title', 'i18n.domain.export-configuration.configurations.delete-dialog.question');
    dialog.onOkSubject.subscribe(() => {
      this.configurationTemplateService.deleteRecord(this.configuration.id).subscribe({
        next: data => {
          this.configurationTemplatesSelectionComponent.refresh();
        }, error: (error) => {
          // we just catch since warnings are handled in service
        }
      });
      this.closeForm();
    });
  }

  public closeForm() {
    this.configurationFormCoordinatorService.configurationDetailsDTO = null;
    this.configurationFormCoordinatorService.configurationDetailsDTOUpdated.next(null);
    this.configurationFormCoordinatorService.resetForms();
    this.configurationTemplatesSelectionComponent.resetRowSelected();
    this.router.navigate([configurationTemplatesExpConfRoute]);
  }

  public saveConfigurationTemplate() {
    this.validateWholeForm();
    if (this.configurationFormCoordinatorService.isWholeFormValid) {
      this.configurationTemplateService.updateRecord(this.getUpdateDTOFromFormData()).subscribe({
        next: data => {
          this.closeForm();
          this.configurationTemplatesSelectionComponent.refresh();
        }, error: (error) => {
          // we just catch since warnings are handled in service
        }
      });
    } else {
      if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
        this.toastService.showWarning('i18n.domain.export-configuration.templates.messages.form-invalid.title',
          'i18n.domain.export-configuration.templates.messages.form-invalid.message', null);
      }
    }
  }

  public exportInterfaceConfigurationSelected(selectedExportInterfaceConfiguration: ExportInterfaceConfigurationDTO) {
    if (selectedExportInterfaceConfiguration?.id != this.selectedTemplateId) {
      if (selectedExportInterfaceConfiguration.id != null) {
        this.router.navigate([configurationTemplatesExpConfRoute + "/" + selectedExportInterfaceConfiguration.id]).then(() => {
          this.configurationFormsOverviewComponent?.getUrlRoute();
        });
      } else {
        this.router.navigate([configurationTemplatesExpConfRoute]);
      }
    }
    this.configurationFormCoordinatorService.loadConfigurationDetails(selectedExportInterfaceConfiguration.id);
  }

  public validateWholeForm() {
    let optionalFormsValid = false;
    this.configurationFormCoordinatorService.isWholeFormValid = false;
    let ignoreFileTabs = false;

    //Email
    if (this.configurationFormGeneral.get('transferType').value === ExportInterfaceConfigurationTransferType.Email) {
      if (this.configurationFormEmail.valid && this.configurationFormTimeplan.valid) {
        optionalFormsValid = true;
      }
    }
    //SFTPServer
    if (this.configurationFormGeneral.get('transferType').value === ExportInterfaceConfigurationTransferType.SFTPServer) {
      if (this.configurationFormTimeplan.valid) {
        optionalFormsValid = true;
      }
    }
    //FTPClient
    if (this.configurationFormGeneral.get('transferType').value === ExportInterfaceConfigurationTransferType.FTPClient) {
      if (this.configurationFormFTP.valid && this.configurationFormTimeplan.valid) {
        optionalFormsValid = true;
      }
    }
    //REST
    if (this.configurationFormGeneral.get('transferType').value === ExportInterfaceConfigurationTransferType.REST) {
      if (this.configurationFormREST.valid) {
        optionalFormsValid = true;
      }
    }
    //SFTPServerOnly
    if (this.configurationFormGeneral.get('transferType').value === ExportInterfaceConfigurationTransferType.SFTPServerOnly) {
      optionalFormsValid = true;
      ignoreFileTabs = true;
    }

    if (this.configurationFormGeneral.get('type').value === ExportInterfaceConfigurationType.CustomerBacklog) {
      if (this.configurationFormCoordinatorService.configurationFormBacklog.valid) {
        optionalFormsValid = true;
      }
      else {
        optionalFormsValid = false;
      }
    }

    if (this.configurationFormGeneral.get('type').value === ExportInterfaceConfigurationType.Finance) {
      if (this.configurationFormCoordinatorService.configurationFormFinance.valid) {
        optionalFormsValid = true;
      }
      else {
        optionalFormsValid = false;
      }
    }

    if (this.configurationFormGeneral.get('type').value === ExportInterfaceConfigurationType.EDIOrder
      || this.configurationFormGeneral.get('type').value === ExportInterfaceConfigurationType.XMLOrder
      || this.configurationFormGeneral.get('type').value === ExportInterfaceConfigurationType.CustomerConnection
      || this.configurationFormGeneral.get('transferType').value === ExportInterfaceConfigurationTransferType.AS2
      || this.configurationFormGeneral.get('type').value === ExportInterfaceConfigurationType.ProductCatalog) {
      optionalFormsValid = true;
      ignoreFileTabs = true;
    }

    // if (this.configurationFormGeneral.valid
    //   && (ignoreFileTabs || this.configurationFormFileName.valid && this.configurationFormFileFilter.valid
    //     && this.configurationFormFileFields.valid) && optionalFormsValid) {
    //   this.configurationFormCoordinatorService.isWholeFormValid = true;
    // }

    if (this.configurationFormGeneral.valid
      && (ignoreFileTabs || this.configurationFormFileName.valid && this.configurationFormFileFilter.valid &&
        (this.configurationFormFileName.get('processType').value === ExportInterfaceConfigurationFileProcessType.ColumnBased && this.configurationFormFileFields.valid) ||
        this.configurationFormFileName.get('processType').value === ExportInterfaceConfigurationFileProcessType.LineBased && this.configurationFormCoordinatorService.configurationFormFileLineBasedAllAttributes.valid)
      && optionalFormsValid) {
      this.configurationFormCoordinatorService.isWholeFormValid = true;
    }
  }

  private getUpdateDTOFromFormData(): UpdateExportInterfaceConfigTemplateDTO {
    return new UpdateExportInterfaceConfigTemplateDTO({
      templateDetails: this.configurationFormCoordinatorService.createConfigurationDetailsDTOFromForm(),
    });
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.configurationFormCoordinatorService.isFormDirty()) {
      const dialog = this.modalService.showOkCancelDialog('i18n.domain.export-configuration.configurations.discard-dialog.title', 'i18n.domain.export-configuration.configurations.discard-dialog.question');
      return merge(
        dialog.onOkSubject.pipe(mapTo(true)),
        dialog.onCancelSubject.pipe(mapTo(false)),
      );
    }
    return true;
  }

}
